import { useCallback, useState } from 'react';

/**
 * A boolean that survives a reload, per key (D10).
 *
 * Which sections a person keeps closed and whether they hide empty fields are
 * PREFERENCES ABOUT LOOKING, not data: they belong to this browser, they are shared
 * with nobody, and a round trip to store them would make opening a section slower than
 * reading it. `localStorage` is the whole mechanism.
 *
 * WRITING IS BEST-EFFORT. Safari in private mode throws on `setItem` and a record page
 * that crashed because it could not remember a chevron would be an absurd trade; the
 * state still works for the session, it just does not survive.
 *
 * The initial read happens in a lazy initializer, so it runs once per mount rather
 * than on every render, and an unparseable or absent entry falls back to the caller's
 * default rather than to `false` - a default of "open" has to stay open.
 */
export function useLocalToggle(key: string, fallback: boolean): [boolean, (next: boolean) => void] {
  const [value, setValue] = useState<boolean>(() => readToggle(key) ?? fallback);

  const set = useCallback(
    (next: boolean) => {
      setValue(next);
      writeToggle(key, next);
    },
    [key],
  );

  return [value, set];
}

/**
 * ONE NUMBER OUT OF A LIST, on the same mechanism (`table-completion.md` D4).
 *
 * Rows per page is a preference about LOOKING, exactly like the three booleans above: it
 * belongs to this browser, it is shared with nobody, and it is a control on the BODY of a
 * screen rather than an entry in a settings menu. So it is one more export in the file that
 * already owns per-browser preference and its key convention, and not a second implementation
 * of `localStorage` reading inside a table module. `useLocalToggle`'s signature is
 * boolean-only, and rows per page is one of four numbers, which is the whole reason there are
 * two exports instead of one.
 *
 * VALIDATED ON READ, against a list the CALLER supplies. A stored value the product no longer
 * offers - a page size that was removed, or anything a person typed into their own storage -
 * degrades to the caller's fallback on `viewState.ts`'s rule ("A VIEW NEVER ERRORS. IT
 * DEGRADES, AND SAYS SO"), with the "says so" arm deliberately absent: the menu then shows 50
 * and the list shows 50, which is consistent rather than a lie.
 *
 * The lazy initializer, the best-effort write and the key convention are the boolean form's,
 * for its reasons: one read per mount rather than one per render, and a table that crashed
 * because Safari in private mode refused to remember a page size would be an absurd trade.
 */
export function useLocalChoice(
  key: string,
  choices: readonly number[],
  fallback: number,
): [number, (next: number) => void] {
  const [value, setValue] = useState<number>(() => readChoice(key, choices) ?? fallback);

  const set = useCallback(
    (next: number) => {
      setValue(next);
      writeChoice(key, next);
    },
    [key],
  );

  return [value, set];
}

/**
 * One key for the WHOLE record page's disclosure state, so the map below is one entry
 * rather than one entry per section - and a user who never touches a chevron leaves no
 * storage behind at all.
 */
export function toggleKey(scope: string, ...parts: readonly string[]): string {
  return ['ofmos', scope, ...parts].join(':');
}

function readToggle(key: string): boolean | undefined {
  try {
    const raw = globalThis.localStorage?.getItem(key);
    if (raw === '1') return true;
    if (raw === '0') return false;
    return undefined;
  } catch {
    return undefined;
  }
}

function writeToggle(key: string, value: boolean): void {
  try {
    globalThis.localStorage?.setItem(key, value ? '1' : '0');
  } catch {
    // Private mode, a full quota, a locked-down browser. The preference is lost, the
    // page is not.
  }
}

/**
 * Absent, unparseable, or no longer on offer: all three are "the caller's default".
 *
 * The stored text is compared against what a write WOULD have produced, rather than parsed and
 * then checked. `Number.parseInt` is the obvious version and it is too generous: it reads
 * `'25.5'` and `'25 rows'` as 25, so a value this hook never wrote comes back as a valid
 * choice.
 */
function readChoice(key: string, choices: readonly number[]): number | undefined {
  try {
    const raw = globalThis.localStorage?.getItem(key);
    return choices.find((choice) => String(choice) === raw);
  } catch {
    return undefined;
  }
}

function writeChoice(key: string, value: number): void {
  try {
    globalThis.localStorage?.setItem(key, String(value));
  } catch {
    // The same trade as the boolean form: the preference is lost, the screen is not.
  }
}
